import { LegalLayout, Section, ShieldCheck } from "./LegalPage";

export default function PrivacyPolicy() {
  return (
    <LegalLayout icon={ShieldCheck} kicker="VITTYYO" title="Privacy Policy" updated="July 19, 2026">
      <p data-testid="privacy-intro">
        This Privacy Policy explains what information Vittyyo ("Vittyyo," "we," "us") collects, how we use it,
        who we share it with, and the choices you have. By creating an account or using the service, you agree to the
        practices described here. If you do not agree, please do not use the service.
      </p>

      <Section heading="1. Information we collect">
        <ul className="list-disc pl-5 space-y-1.5">
          <li><span className="font-semibold">Account information:</span> your email address, chosen username, your name,
            and a securely hashed password. If you sign in with Google, we receive your email, name, and profile picture
            from Google.</li>
          <li><span className="font-semibold">Content you create:</span> the goals you type, the manuals, step
            instructions, images, voice-overs, and videos generated for you, and anything you choose to share to the
            public Community board.</li>
          <li><span className="font-semibold">Purchase records:</span> when you buy credits, our payment
            processor (Stripe) handles your card details. We do not receive or store your full card number — we retain a
            record of the transaction (amount, date, status).</li>
          <li><span className="font-semibold">Consent and legal records:</span> when you accept an in-app disclaimer,
            we store a timestamped record and your IP address.</li>
          <li><span className="font-semibold">Technical data:</span> a login token and small preferences are kept in your
            browser's local storage to keep you signed in and remember settings.</li>
        </ul>
      </Section>

      <Section heading="2. How we use your information">
        <p>We use your information to operate and improve the service: to create your account, generate your manuals and
          media, process payments, remember your preferences, maintain records of consent, keep the service safe and
          family-friendly, and communicate with you (for example, a welcome email).</p>
      </Section>

      <Section heading="3. AI providers and how your prompts are processed">
        <p>To generate content, we send your text prompts and image-generation requests to third-party AI providers.
          Text is processed by <span className="font-semibold">OpenAI</span>; images are generated by
          <span className="font-semibold"> Google (Gemini)</span>; voice-overs use OpenAI's text-to-speech. These
          requests are routed through a managed integration service (Emergent). Each provider handles data under its own
          policies, which we do not control. We encourage you to review the
          {" "}<a href="https://openai.com/policies/terms-of-use" target="_blank" rel="noopener noreferrer" className="text-[#5d8a52] underline font-semibold">OpenAI terms</a>{" "}
          and{" "}<a href="https://ai.google.dev/gemini-api/terms" target="_blank" rel="noopener noreferrer" className="text-[#5d8a52] underline font-semibold">Google Gemini terms</a>.</p>
      </Section>

      <Section heading="4. Who we share information with">
        <p>We share information only with service providers that help us run Vittyyo: AI providers (OpenAI, Google) and
          the Emergent integration service; our payment processor (Stripe); our email provider (Resend); and, if you use
          shopping links, Amazon (as an Amazon Associate we may earn from qualifying purchases). We do not sell your
          personal information. We may disclose information if required by law or to protect the service and its users.</p>
      </Section>

      <Section heading="5. Data retention and your choices">
        <p>We keep your account and content until you delete them or close your account. You can delete individual manuals
          at any time. To request access to or deletion of your personal information, contact us using the details below.
          Depending on where you live, you may have additional rights under laws such as the GDPR or CCPA/CPRA.</p>
      </Section>

      <Section heading="6. Children">
        <p>Vittyyo is not directed to children. You must meet the minimum age required in your country to use the
          service. We do not knowingly collect personal information from children.</p>
      </Section>

      <Section heading="7. Security">
        <p>We use industry-standard measures (including encrypted transport and hashed passwords) to protect your
          information. No method of transmission or storage is 100% secure, and we cannot guarantee absolute security.</p>
      </Section>

      <Section heading="8. Changes to this policy">
        <p>We may update this Privacy Policy from time to time. When we do, we will revise the "Last updated" date above.
          Your continued use of the service after changes take effect constitutes acceptance of the updated policy.</p>
      </Section>

      <Section heading="9. Contact us">
        <p>Questions about privacy? Contact us at <span className="font-semibold">privacy@stepmystep.com</span>.</p>
      </Section>
    </LegalLayout>
  );
}
